/**
 * The poster temperature (Phase 10 plan §4.2): its text, and a font size that
 * fits the sidebar rather than a sidebar that fits it.
 *
 * 1B draws "73°" at 176 px in a 420 px sidebar, and the sidebar is
 * `minmax(320px, 33vw)`. The hero is a CSS container and the size is
 * `min(176px, (100cqi - padding) / width-in-em)`, with the width in em taken
 * from the string actually shown. One constant for the widest case would
 * shrink every two-digit temperature at 320 px to make room for a "104°" that
 * is not on screen.
 */

/**
 * Archivo at `wght` 800, in em, measured in Chromium off the vendored woff2
 * (2026-09-27): a digit (tabular, so every digit), U+2212, and U+00B0.
 */
export const ADVANCE_EM = { digit: 0.627, minus: 0.649, degree: 0.4 } as const

/** 1B's tracking on the poster numerals. Chromium adds it after every glyph. */
export const TRACKING_EM = -0.06

/** The largest the numerals are drawn. */
export const HERO_MAX_PX = 176

/** The hero's horizontal padding around the numerals: 1B's 16 left and 24 right. */
export const HERO_PAD_PX = 16 + 24

/** "73°", "−12°" with a true minus (U+2212), or "—" for no reading. */
export function heroTemp(f: number | null | undefined): string {
  if (f == null || !Number.isFinite(f)) return '—'
  const n = Math.round(f)
  // `Math.round(-0.4)` is -0, which prints as "0" and must not grow a minus.
  return `${n < 0 ? '−' : ''}${Math.abs(n)}°`
}

/** The drawn width of `text` in em, tracking included. */
export function heroWidthEm(text: string): number {
  let em = 0
  for (const ch of text) {
    em +=
      ch === '−'
        ? ADVANCE_EM.minus
        : ch === '°'
          ? ADVANCE_EM.degree
          : ADVANCE_EM.digit
    em += TRACKING_EM
  }
  return em
}

/**
 * The phone's NOW block (Phase 11 plan §5.2): the redesign's 128 px square
 * with the numerals at 58 px, padded 14 each side. "73°" fits at the full
 * size (85 px against 100), and "104°" is fitted to about 49.
 */
export const PHONE_NOW = { maxPx: 58, padPx: 14 + 14 } as const

/**
 * Phase 11's NOW block: the redesign draws "58°" at 66 px in a 140 px square
 * padded 16 each side. "73°" fits at the full size (97 px against 108), and
 * "104°" is fitted to about 53.
 */
export const NOW_BLOCK = { maxPx: 66, padPx: 16 + 16 } as const

/**
 * The CSS font size for `text` in a container whose inline size is `100cqi`:
 * as large as fits beside the padding, and never above the maximum (the
 * sidebar's 176 px unless told otherwise). Rounded up to three decimals so a
 * width that exactly fits cannot lose the last pixel to rounding.
 */
export function heroFontSize(
  text: string,
  { maxPx = HERO_MAX_PX, padPx = HERO_PAD_PX }: { maxPx?: number; padPx?: number } = {},
): string {
  const em = Math.ceil(heroWidthEm(text) * 1000) / 1000
  if (em <= 0) return `${maxPx}px`
  return `min(${maxPx}px, calc((100cqi - ${padPx}px) / ${em}))`
}
